// Step 2 (FE-4): the rows of the columns step and the writes "Continua" turns
// them into. A row is a column → analysis mapping or an ignored column, either
// saved (`id`) or a draft (`id: null`). Nothing is written before "Continua"
// (C46).

export const PAGE_SIZE = 20;
export const DEFAULT_DECIMALS = 2;
export const MAX_DECIMALS = 6;

export const MAPPING_INCLUDE = Object.freeze(['analysis', 'measure']);
export const MAPPING_ORDER = Object.freeze(['sourceColumn ASC', 'id ASC']);

// What the search box matches: the source column, or the analysis by code
// or description.
export const SEARCH_FIELDS = Object.freeze({
  COLUMN: 'column',
  ANALYSIS: 'analysis',
});

export const ROW_ACTIONS = Object.freeze({
  DUPLICATE: 'duplicate',
  IGNORE: 'ignore',
  MAP: 'map',
  DEACTIVATE: 'deactivate',
  REMOVE: 'remove',
  UNDO: 'undo',
});

export const OPERATIONS = Object.freeze({
  DELETE: 'delete',
  PATCH: 'patch',
  CREATE: 'create',
  DEACTIVATE_COLUMN: 'deactivateColumn',
});

// Field error codes of `rowErrors`.
export const FIELD_ERRORS = Object.freeze({
  REQUIRED: 'required',
  LD_THRESHOLD: 'ldThreshold',
  DECIMALS: 'decimals',
});

// LD_THRESHOLD_PATTERN of the backend model: a positive decimal, dot separated.
const LD_THRESHOLD_PATTERN = /^(?!0+(?:\.0+)?$)\d+(?:\.\d+)?$/;

// On a threshold already normalized (`normalizeLdThreshold`).
export const isValidLdThreshold = (ldThreshold) =>
  LD_THRESHOLD_PATTERN.test(ldThreshold);

const EDITABLE_FIELDS = Object.freeze([
  'analysisId',
  'ldThreshold',
  'decimals',
  'measureId',
]);

export const savedKey = (id) => `saved-${id}`;
export const pendingIgnoredKey = (sourceColumn) => `ignore-${sourceColumn}`;

export function savedRow(mapping) {
  return { ...mapping, key: savedKey(mapping.id) };
}

// `afterKey`: the row the draft is shown after, null for a row of its own
// (an unmapped file column). `required`: written even if left as created, so
// it has to be completed or removed.
export function draftRow(
  key,
  { sourceColumn, afterKey = null, required = false, fields = {} }
) {
  return {
    key,
    id: null,
    sourceColumn,
    ignored: false,
    analysisId: null,
    analysis: null,
    ldThreshold: null,
    decimals: DEFAULT_DECIMALS,
    measureId: null,
    measure: null,
    ...fields,
    afterKey,
    required,
  };
}

// A duplicate keeps the column's parameters; the analysis is to choose, since
// the same column × analysis pair already exists (C45).
export function duplicateFields({ ldThreshold, decimals, measureId, measure }) {
  return { ldThreshold, decimals, measureId, measure };
}

// A column marked as ignored, not saved yet.
function pendingIgnoredRow(sourceColumn) {
  return {
    key: pendingIgnoredKey(sourceColumn),
    id: null,
    sourceColumn,
    ignored: true,
    pending: true,
  };
}

// The decimal comma is accepted and sent as a dot.
export function normalizeLdThreshold(value) {
  return value === null || value === undefined
    ? ''
    : String(value).trim().replace(',', '.');
}

const isBlankDraft = (row) =>
  row.analysisId === null &&
  normalizeLdThreshold(row.ldThreshold) === '' &&
  row.measureId === null &&
  row.decimals === DEFAULT_DECIMALS;

// The limits the backend enforces (C25, BE-1), by field; empty when valid.
export function rowErrors(row) {
  const errors = {};
  const ldThreshold = normalizeLdThreshold(row.ldThreshold);
  if (row.analysisId === null) errors.analysisId = FIELD_ERRORS.REQUIRED;
  if (ldThreshold === '') {
    errors.ldThreshold = FIELD_ERRORS.REQUIRED;
  } else if (!isValidLdThreshold(ldThreshold)) {
    errors.ldThreshold = FIELD_ERRORS.LD_THRESHOLD;
  }
  if (
    !Number.isInteger(row.decimals) ||
    row.decimals < 0 ||
    row.decimals > MAX_DECIMALS
  ) {
    errors.decimals = FIELD_ERRORS.DECIMALS;
  }
  if (row.measureId === null) errors.measureId = FIELD_ERRORS.REQUIRED;
  return errors;
}

const isValid = (row) => Object.keys(rowErrors(row)).length === 0;

// Only the fields that differ from the saved mapping.
export function changedFields(saved, row) {
  return EDITABLE_FIELDS.reduce((changes, field) => {
    const value =
      field === 'ldThreshold' ? normalizeLdThreshold(row[field]) : row[field];
    return value === saved[field] ? changes : { ...changes, [field]: value };
  }, {});
}

function createPayload(row) {
  return {
    sourceColumn: row.sourceColumn,
    analysisId: row.analysisId,
    ldThreshold: normalizeLdThreshold(row.ldThreshold),
    decimals: row.decimals,
    measureId: row.measureId,
  };
}

// Characters with a meaning in a JS RegExp. The backend (juggler
// `sanitizeQuery`) rewrites a `regexp` or `ilike` string that is not a valid
// RegExp, adding backslashes of its own (C47): escaped, the string always is.
const REGEXP_SPECIALS = /[\\^$.*+?()[\]{}|/-]/g;

// For `analyses.autocompleteFetch`, which wraps the text in `/…/i`: the text
// is matched literally.
export function escapeRegExp(text) {
  return text.replace(REGEXP_SPECIALS, '\\$&');
}

// For `ilike`, written by the connector as `ILIKE ? ESCAPE '\'`, where `\x`
// is a literal `x` for any `x`: `%` and `_` are escaped as wildcards, the
// RegExp specials so that the backend leaves the string as sent (C47).
export function escapeLike(text) {
  return escapeRegExp(text).replace(/[%_]/g, '\\$&');
}

// `search`: `{column}` or `{analysisIds}`, `{}` without search.
export function matchesSearch(row, search) {
  if (search.analysisIds) return search.analysisIds.includes(row.analysisId);
  if (search.column) {
    return row.sourceColumn.toLowerCase().includes(search.column.toLowerCase());
  }
  return true;
}

// Active rows of the file columns (group 2, C44), read in full. By column, not
// by the `mappingIds` of prepare: rows written on this step stay in the group.
export function fileRowsFilter(sourceColumns) {
  return {
    where: { active: true, sourceColumn: { inq: sourceColumns } },
    include: MAPPING_INCLUDE,
    order: MAPPING_ORDER,
  };
}

// Every other active row (group 3, C44); `nin: []` adds no condition (C47).
export function otherRowsWhere(fileColumns, search) {
  const conditions = [{ active: true }, { sourceColumn: { nin: fileColumns } }];
  if (search.analysisIds) {
    conditions.push({ analysisId: { inq: search.analysisIds } });
  } else if (search.column) {
    conditions.push({
      sourceColumn: { ilike: `%${escapeLike(search.column)}%` },
    });
  }
  return { and: conditions };
}

// Groups 1 and 2 follow the file: by column position, then as saved.
export function sortByFileColumn(rows, fileColumns) {
  const position = (row) => {
    const index = fileColumns.indexOf(row.sourceColumn);
    return index === -1 ? fileColumns.length : index;
  };
  return [...rows].sort((a, b) => position(a) - position(b));
}

export function otherRowsFilter(where, { skip, limit }) {
  return {
    where,
    include: MAPPING_INCLUDE,
    order: MAPPING_ORDER,
    skip,
    limit,
  };
}

// One page of a single list: the local rows (groups 1 and 2) first, then the
// server rows (group 3) from `skip`.
export function pageWindow(page, localCount) {
  const start = (page - 1) * PAGE_SIZE;
  const localEnd = Math.min(start + PAGE_SIZE, localCount);
  const localRows = Math.max(0, localEnd - start);
  return {
    localStart: Math.min(start, localCount),
    localEnd,
    skip: Math.max(0, start - localCount),
    limit: PAGE_SIZE - localRows,
  };
}

// Each row followed by the drafts duplicated from it, depth first.
function withDrafts(rows, drafts) {
  const expand = (row) => [
    row,
    ...drafts.filter(({ afterKey }) => afterKey === row.key).flatMap(expand),
  ];
  return rows.flatMap(expand);
}

// The rows as shown: drafts after their row, deactivated rows left out, the
// rows of a column being ignored collapsed into one, the ignored row of a
// column being mapped replaced by its drafts.
export function arrangeRows(rows, { drafts, removedIds, columnChanges }) {
  const collapsed = new Set();
  return withDrafts(rows, drafts).reduce((arranged, row) => {
    if (row.id !== null && removedIds.includes(row.id)) return arranged;
    const change = columnChanges[row.sourceColumn];
    if (change && change.ignored) {
      if (collapsed.has(row.sourceColumn)) return arranged;
      collapsed.add(row.sourceColumn);
      return [...arranged, pendingIgnoredRow(row.sourceColumn)];
    }
    if (change && row.id !== null && row.id === change.ignoredRowId) {
      return arranged;
    }
    return [...arranged, row];
  }, []);
}

export function rowActions(row, change) {
  const { DUPLICATE, IGNORE, MAP, DEACTIVATE, REMOVE, UNDO } = ROW_ACTIONS;
  if (row.pending) return [UNDO];
  if (row.ignored) return [MAP, DEACTIVATE];
  if (change && !change.ignored) {
    return change.anchorKey === row.key
      ? [DUPLICATE, UNDO]
      : [DUPLICATE, REMOVE];
  }
  if (row.id !== null) return [DUPLICATE, IGNORE, DEACTIVATE];
  return row.required ? [DUPLICATE, IGNORE, REMOVE] : [DUPLICATE, IGNORE];
}

// Everything "Continua" writes (C46). `edits`: `{[id]: {saved, row}}`;
// `removed`: saved rows to deactivate; `columnChanges`: `{[sourceColumn]:
// {ignored: true} | {ignored: false, ignoredRowId, anchorKey}}`.
export function pendingWrites({ drafts, edits, removed, columnChanges }) {
  const isIgnoring = (sourceColumn) =>
    Boolean(columnChanges[sourceColumn] && columnChanges[sourceColumn].ignored);
  const removedIds = removed.map(({ id }) => id);
  return {
    removals: removed.filter(({ sourceColumn }) => !isIgnoring(sourceColumn)),
    patches: Object.values(edits)
      .filter(
        ({ saved }) =>
          !isIgnoring(saved.sourceColumn) && !removedIds.includes(saved.id)
      )
      .map(({ saved, row }) => ({ row, changes: changedFields(saved, row) }))
      .filter(({ changes }) => Object.keys(changes).length > 0),
    ignoredColumns: Object.keys(columnChanges).filter(isIgnoring),
    unignoredColumns: Object.keys(columnChanges)
      .filter((sourceColumn) => !isIgnoring(sourceColumn))
      .map((sourceColumn) => ({
        sourceColumn,
        ...columnChanges[sourceColumn],
      })),
    creates: drafts.filter(
      (row) =>
        !isIgnoring(row.sourceColumn) && (row.required || !isBlankDraft(row))
    ),
  };
}

// The file columns (prepare `columns`) still neither mapped nor ignored once
// the writes are done: no new mapping, not being ignored, and every row they
// had deactivated (C59). Their values are not imported.
export function columnsLeftUnmapped(columns, writes, removedIds) {
  return columns
    .filter(
      ({ sourceText, mappingIds }) =>
        !writes.ignoredColumns.includes(sourceText) &&
        !writes.creates.some((row) => row.sourceColumn === sourceText) &&
        mappingIds.every((id) => removedIds.includes(id))
    )
    .map(({ sourceText }) => sourceText);
}

export function countWrites(writes) {
  return (
    writes.removals.length +
    writes.patches.length +
    writes.ignoredColumns.length +
    writes.creates.length
  );
}

// The rows to write that break a field rule, in the order they are written.
export function invalidRows(writes) {
  return [...writes.patches.map(({ row }) => row), ...writes.creates].filter(
    (row) => !isValid(row)
  );
}

// The writes in order: deactivations, edits, column state changes (the active
// rows of the column first, then the new one), new mappings. `key`: the row
// that shows the outcome; `sourceColumn`: after a failure, the later writes
// of the same column are skipped.
export function buildOperations(writes) {
  const creates = (sourceColumn) =>
    writes.creates.filter((row) => row.sourceColumn === sourceColumn);
  const unignored = writes.unignoredColumns.map(
    ({ sourceColumn }) => sourceColumn
  );
  const createOperation = (row) => ({
    type: OPERATIONS.CREATE,
    key: row.key,
    sourceColumn: row.sourceColumn,
    data: createPayload(row),
  });
  return [
    ...writes.removals.map(({ id, key, sourceColumn }) => ({
      type: OPERATIONS.DELETE,
      key,
      sourceColumn,
      id,
    })),
    ...writes.patches.map(({ row, changes }) => ({
      type: OPERATIONS.PATCH,
      key: row.key,
      sourceColumn: row.sourceColumn,
      id: row.id,
      data: changes,
    })),
    ...writes.ignoredColumns.flatMap((sourceColumn) => [
      {
        type: OPERATIONS.DEACTIVATE_COLUMN,
        key: pendingIgnoredKey(sourceColumn),
        sourceColumn,
      },
      {
        type: OPERATIONS.CREATE,
        key: pendingIgnoredKey(sourceColumn),
        sourceColumn,
        data: { sourceColumn, ignored: true },
      },
    ]),
    ...writes.unignoredColumns.flatMap(
      ({ sourceColumn, ignoredRowId, anchorKey }) => [
        ...(ignoredRowId === null
          ? []
          : [
              {
                type: OPERATIONS.DELETE,
                key: anchorKey,
                sourceColumn,
                id: ignoredRowId,
              },
            ]),
        ...creates(sourceColumn).map(createOperation),
      ]
    ),
    ...writes.creates
      .filter(({ sourceColumn }) => !unignored.includes(sourceColumn))
      .map(createOperation),
  ];
}
